import { memo, useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert, Box, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { FormContainer } from 'components/layout';
import { Checkbox, ChipInput, SelectableCampaignsGrid } from 'components/common';
import datadogActions from 'lib/datadog';
import { useLocationParams } from 'hooks';
import type { TwitterCampaignEntry } from '../api';
import useTwitterCampaignsData from '../hooks/useTwitterCampaignsData';
import { formatEntityStatus } from '../utils/entityStatus';
import { toIdSelectionEntries } from '../utils/idSelection';

export const TWITTER_CAMPAIGNS_LIMIT = 20;
const EMPTY: string[] = [];

const COLUMNS: GridColDef<TwitterCampaignEntry>[] = [
  { field: 'campaign_id', headerName: 'Campaign ID', width: 140 },
  { field: 'campaign_name', headerName: 'Campaign Name', width: 700 },
  {
    field: 'status',
    headerName: 'Status',
    width: 110,
    renderCell: (params) => (
      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        {formatEntityStatus(params.row.status)}
      </Typography>
    ),
  },
];

const ChooseCampaigns = memo(() => {
  const {
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();
  const selectedCampaigns = (useWatch({ name: 'selectedCampaigns' }) as string[] | undefined) ?? EMPTY;
  const filterByName = useWatch({ name: 'filterCampaignsByName' }) as string[] | undefined;
  const campaignsError = errors.selectedCampaigns?.message as string | undefined;
  const {
    setSelectedCampaigns: setSelectedCampaignsParam,
    setSelectedAdSets: setSelectedAdSetsParam,
    setFilterCampaignsByName: setFilterCampaignsByNameParam,
    setAdSettingsFlow: setAdSettingsFlowParam,
  } = useLocationParams();

  const { gridRows, isLoading, isError, errorMessage } = useTwitterCampaignsData();

  const handleSelectionChange = useCallback(
    (ids: string[]) => {
      setValue('selectedCampaigns', ids, { shouldValidate: true, shouldDirty: true });
      setValue('selectedLineItems', [], { shouldValidate: true });
      setValue('adSettings', {});
      setSelectedAdSetsParam([]);
      setAdSettingsFlowParam({});
    },
    [setValue, setSelectedAdSetsParam, setAdSettingsFlowParam],
  );

  useEffect(() => {
    setSelectedCampaignsParam(toIdSelectionEntries(selectedCampaigns));
  }, [selectedCampaigns, setSelectedCampaignsParam]);

  useEffect(() => {
    setFilterCampaignsByNameParam(filterByName ?? []);
  }, [filterByName, setFilterCampaignsByNameParam]);

  const getRowId = useCallback((row: TwitterCampaignEntry) => row.campaign_id, []);

  useEffect(() => {
    void trigger('selectedCampaigns');
  }, [trigger]);

  useEffect(() => {
    if (!isError) return;

    datadogActions.trackDatadogError(new Error(errorMessage ?? 'Failed to load campaigns'), {
      source: 'twitter_choose_campaigns_query',
    });
  }, [isError, errorMessage]);

  const limitMessage = useMemo(
    () => `Up to ${TWITTER_CAMPAIGNS_LIMIT} campaigns can be selected`,
    [],
  );

  return (
    <FormContainer
      title="Choose Campaigns"
      error={campaignsError}
      sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, height: '100%' }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%' }}>
        <Box sx={{ flex: 1, maxWidth: '50%' }}>
          <ChipInput name="filterCampaignsByName" inputLabel="Enter campaign name" />
        </Box>
        <Checkbox
          dataTestId="selectedCampaignsOnly"
          name="selectedCampaignsOnly"
          label="Selected only"
          controlLabelSx={{ whiteSpace: 'nowrap' }}
        />
      </Box>
      {campaignsError ? (
        <Typography variant="caption" color="error" role="alert">
          {campaignsError}
        </Typography>
      ) : null}

      {isError ? (
        <Alert severity="error" sx={{ width: '100%' }} data-testid="twitter-campaigns-error">
          {errorMessage}
        </Alert>
      ) : (
        <SelectableCampaignsGrid
          rows={gridRows}
          columns={COLUMNS}
          getRowId={getRowId}
          selectedIds={selectedCampaigns}
          onSelectionChange={handleSelectionChange}
          limit={TWITTER_CAMPAIGNS_LIMIT}
          limitMessage={limitMessage}
          isLoading={isLoading}
        />
      )}
    </FormContainer>
  );
});

ChooseCampaigns.displayName = 'ChooseCampaigns';

export default ChooseCampaigns;
